"use client";
import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { 
  Calendar, 
  Clock, 
  User, 
  ArrowLeft, 
  BookOpen, 
  Sparkles,
  ArrowRight
} from 'lucide-react';

// استيراد مكونات Shadcn UI
import { Button } from "@/components/ui/button";
import { BLOGS_DATA } from '../_Components/Blogs/blogsData';

// مكون كارت المقال العادي
const BlogCard = ({ post, delay }) => {
  return (
    <motion.div
      initial={{ y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.5 }}
      className="bg-white/80 backdrop-blur-md border border-slate-100 rounded-[2rem] shadow-[0_15px_40px_-15px_rgba(0,0,0,0.03)] hover:shadow-[0_30px_60px_-15px_rgba(0,74,173,0.12)] flex flex-col justify-between h-full transition-all duration-500 group/blog overflow-hidden w-full relative z-10"
    >
      <div className="flex flex-col flex-1">
        {/* صورة المقال */}
        <div className="w-full h-48 sm:h-52 overflow-hidden relative border-b border-slate-50">
          <div className="absolute top-4 right-4 z-20 bg-white/90 backdrop-blur-md text-[#004aad] text-xs font-black px-3 py-1.5 rounded-xl shadow-sm border border-slate-100">
            {post.category}
          </div>
          <img 
            src={post.imageSrc} 
            alt={post.title} 
            className="w-full h-full object-cover transition-transform duration-700 group-hover/blog:scale-105"
          />
        </div>

        {/* محتوى الكارت */}
        <div className="p-6 space-y-4 flex-1 flex flex-col justify-between">
          <div className="space-y-3">
            {/* الميتا داتا */}
            <div className="flex items-center gap-4 text-gray-400 text-[11px] sm:text-xs font-bold">
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                <span>{post.readTime}</span>
              </div>
            </div>

            {/* العنوان */}
            <h3 className="text-base sm:text-lg font-black text-gray-900 tracking-tight leading-snug group-hover/blog:text-[#004aad] transition-colors duration-300 line-clamp-2 min-h-[3.25rem]">
              {post.title}
            </h3>

            {/* الوصف المختصر */}
            <p className="text-gray-600 text-xs sm:text-[13px] leading-relaxed font-medium line-clamp-3">
              {post.desc}
            </p>
          </div>

          {/* الكاتب وزر القراءة */}
          <div className="pt-4 border-t border-slate-100 flex items-center justify-between gap-2 mt-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs font-bold">
              <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[#004aad]">
                <User className="w-3 h-3" />
              </div>
              <span className="truncate max-w-[100px]">{post.author}</span>
            </div>

            <Link href={`/blog/${post.id}`} passHref legacyBehavior>
              <Button 
                variant="secondary" 
                size="sm"
                className="text-xs font-black text-[#004aad] bg-blue-50/50 hover:bg-blue-50 rounded-xl group/btn gap-1.5 px-3 h-9 cursor-pointer"
              >
                <span>اقرأ المقال</span>
                <ArrowLeft className="w-3.5 h-3.5 transition-transform duration-300 group-hover/btn:-translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default function BlogsPage() {
  return (
    <main className="min-h-screen bg-slate-50/50 font-sans pb-16 md:pb-24 relative overflow-hidden" dir="rtl">
      
      {/* عناصر الديكور بالخلفية */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-100/30 rounded-full blur-[120px]" />
        <div className="absolute bottom-20 right-1/4 w-[400px] h-[400px] bg-purple-100/20 rounded-full blur-[100px]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#f1f5f9_1px,transparent_1px),linear-gradient(to_bottom,#f1f5f9_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-70" />
      </div>

      {/* الهيدر العلوي */}
      <header className="w-full bg-white/75 backdrop-blur-md border-b border-slate-100 py-4 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link 
            href="/" 
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200/80 text-gray-700 hover:text-gray-900 font-black text-xs sm:text-sm rounded-xl transition-all active:scale-[0.98] border border-slate-200/50 shadow-sm"
          >
            <ArrowRight className="w-4 h-4" />
            <span>العودة للرئيسية</span>
          </Link>
          
          <div className="flex items-center gap-2">
            <span className="font-extrabold text-[#004aad] font-mono text-sm sm:text-base tracking-wide">LASERJET</span>
            <span className="w-1.5 h-1.5 rounded-full bg-[#3fd5b7]" />
            <span className="text-[10px] sm:text-xs font-black text-gray-400">المدونة</span>
          </div>
        </div>
      </header>

      {/* محتوى الصفحة */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8  md:mt-16 space-y-12 relative z-10">
        
        {/* هيدر العناوين */}
        <div className="text-center max-w-2xl mx-auto space-y-3 md:space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-[#004aad] text-xs font-black rounded-xl border border-blue-100/50 shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-[#3fd5b7]" />
            <span>مدونة ليزرجيت الرسمية</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 tracking-tight leading-tight">
            آخر الأخبار و<span className="text-[#004aad]">المقالات</span> الحصرية
          </h1>
          <p className="text-xs sm:text-sm md:text-[15px] text-gray-500 font-bold max-w-lg mx-auto leading-relaxed">
            اكتشف شروحات تقنية، أدلة استثمارية، ونصائح تشطيب هندسية متكاملة مقدمة من خبراء المنصة.
          </p>
          <div className="w-20 sm:w-28 h-1 bg-gradient-to-r from-transparent via-[#004aad] to-transparent mx-auto rounded-full mt-2" />
        </div>

        {/* شبكة المقالات الكاملة مباشرة */}
        <div className="space-y-6 sm:space-y-8">
          <h3 className="text-lg sm:text-xl font-black text-gray-900 tracking-tight flex items-center gap-2 border-r-4 border-[#004aad] pr-2.5">
            <BookOpen className="w-5 h-5 text-[#004aad]" />
            <span>كافة المقالات المتاحة ({BLOGS_DATA.length})</span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 items-stretch">
            {BLOGS_DATA.map((post, index) => (
              <div key={post.id} className="flex w-full">
                <BlogCard post={post} delay={index * 0.05} />
              </div>
            ))}
          </div>
        </div>

      </div>
    </main>
  );
}